import localforage from 'localforage';
import type { Album } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Playlist } from '../../interfaces/playlists';
import type { LibraryComposer } from '../../interfaces/composer';
import type { LibraryRecording } from '../../interfaces/recording';
import type { LibraryWork } from '../../interfaces/work';

export type LibraryLists = {
  albums?: Album[];
  artists?: Artist[];
  playlists?: Playlist[];
  composers?: LibraryComposer[];
  works?: LibraryWork[];
  recordings?: LibraryRecording[];
};

type CachedPayload = LibraryLists & { version: number };

const BOOT_KEY = (userId: string) => `cmf.library.${userId}.boot`;
const FULL_KEY = (userId: string) => `cmf.library.${userId}.full`;
const FULL_WORKS_KEY = (userId: string) => `cmf.library.${userId}.full.works`;
const COMPAT_VERSIONS = new Set([1, 2]);
const VERSION = 2;
const BOOT_TOP_N = 50;
const COMPOSER_CACHE_KEY = 'cmf.catalog-composers.v1';

const safeSlice = <T>(items: T[] | undefined, limit: number): T[] | undefined => {
  if (!Array.isArray(items)) return undefined;
  if (limit <= 0) return [];
  return items.slice(0, limit);
};

const buildPayload = (lists: LibraryLists, topN?: number): CachedPayload => ({
  version: VERSION,
  albums: topN ? safeSlice(lists.albums, topN) : lists.albums,
  artists: topN ? safeSlice(lists.artists, topN) : lists.artists,
  playlists: topN ? safeSlice(lists.playlists, topN) : lists.playlists,
  composers: topN ? safeSlice(lists.composers, topN) : lists.composers,
  works: topN ? safeSlice(lists.works, topN) : lists.works,
  recordings: topN ? safeSlice(lists.recordings, topN) : lists.recordings,
});

export const libraryCache = {
  async persistBoot(userId: string, lists: LibraryLists): Promise<void> {
    if (!userId) return;
    const payload = buildPayload(lists, BOOT_TOP_N);
    await localforage.setItem(BOOT_KEY(userId), payload);
  },
  async persistFull(userId: string, lists: LibraryLists): Promise<void> {
    if (!userId) return;
    const payload = buildPayload({
      albums: lists.albums,
      artists: lists.artists,
      playlists: lists.playlists,
      composers: lists.composers,
    });
    await localforage.setItem(FULL_KEY(userId), payload);
  },
  async persistFullWorksRecordings(userId: string, lists: LibraryLists): Promise<void> {
    if (!userId) return;
    const payload = buildPayload({
      works: lists.works,
      recordings: lists.recordings,
    });
    await localforage.setItem(FULL_WORKS_KEY(userId), payload);
  },
  async loadBoot(userId: string): Promise<LibraryLists | null> {
    if (!userId) return null;
    try {
      const payload = (await localforage.getItem<CachedPayload>(BOOT_KEY(userId))) ?? null;
      const version = payload?.version ?? 1;
      if (!payload || !COMPAT_VERSIONS.has(version)) return null;
      return {
        albums: payload.albums ?? [],
        artists: payload.artists ?? [],
        playlists: payload.playlists ?? [],
        composers: payload.composers ?? [],
        works: payload.works ?? [],
        recordings: payload.recordings ?? [],
      };
    } catch {
      return null;
    }
  },
  async loadFull(userId: string): Promise<LibraryLists | null> {
    if (!userId) return null;
    try {
      const payload = (await localforage.getItem<CachedPayload>(FULL_KEY(userId))) ?? null;
      const version = payload?.version ?? 1;
      if (!payload || !COMPAT_VERSIONS.has(version)) return null;
      return {
        albums: payload.albums ?? [],
        artists: payload.artists ?? [],
        playlists: payload.playlists ?? [],
        composers: payload.composers ?? [],
      };
    } catch {
      return null;
    }
  },
  async loadFullWorksRecordings(userId: string): Promise<LibraryLists | null> {
    if (!userId) return null;
    try {
      const payload = (await localforage.getItem<CachedPayload>(FULL_WORKS_KEY(userId))) ?? null;
      if (!payload || payload.version !== VERSION) return null;
      return {
        works: payload.works ?? [],
        recordings: payload.recordings ?? [],
      };
    } catch {
      return null;
    }
  },
  async clear(userId: string): Promise<void> {
    if (!userId) return;
    const keys = [
      localforage.removeItem(BOOT_KEY(userId)),
      localforage.removeItem(FULL_KEY(userId)),
      localforage.removeItem(FULL_WORKS_KEY(userId)),
      localforage.removeItem(COMPOSER_CACHE_KEY),
    ];

    // Clear any mirrored localStorage entries best-effort
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(BOOT_KEY(userId));
        window.localStorage.removeItem(FULL_KEY(userId));
        window.localStorage.removeItem(FULL_WORKS_KEY(userId));
        window.localStorage.removeItem(COMPOSER_CACHE_KEY);
      }
    } catch {
      // ignore
    }

    await Promise.allSettled(keys);
  },
};
